import { ref } from "vue";

export interface Address {
  id: string;
  user_id: string;
  name?: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export function useAddresses() {
  const addresses = ref<Address[]>([]);
  const isLoading = ref(false);

  const fetchAddresses = async () => {
    isLoading.value = true;
    try {
      const response = await $fetch<{ addresses: Address[] }>("/api/addresses");
      addresses.value = response.addresses || [];
    } catch (e) {
      console.error("Failed to fetch addresses:", e);
    } finally {
      isLoading.value = false;
    }
  };

  const addAddress = async (addressData: Partial<Address>) => {
    try {
      const response = await $fetch<{ address: Address }>("/api/addresses", {
        method: "POST",
        body: addressData,
      });
      if (response.address.is_default) {
        addresses.value.forEach(a => a.is_default = false);
      }
      addresses.value.unshift(response.address);
      return response.address;
    } catch (e) {
      console.error("Failed to add address:", e);
      throw e;
    }
  };

  const updateAddress = async (id: string, updates: Partial<Address>) => {
    try {
      const response = await $fetch<{ address: Address }>(`/api/addresses/${id}`, {
        method: "PUT",
        body: updates,
      });
      if (response.address.is_default) {
        addresses.value.forEach(a => a.is_default = false);
      }
      const index = addresses.value.findIndex(a => a.id === id);
      if (index !== -1) {
        addresses.value[index] = response.address;
      }
      return response.address;
    } catch (e) {
      console.error("Failed to update address:", e);
      throw e;
    }
  };

  const deleteAddress = async (id: string) => {
    try {
      await $fetch(`/api/addresses/${id}`, {
        method: "DELETE",
      });
      addresses.value = addresses.value.filter(a => a.id !== id);
    } catch (e) {
      console.error("Failed to delete address:", e);
      throw e;
    }
  };

  return {
    addresses,
    isLoading,
    fetchAddresses,
    addAddress,
    updateAddress,
    deleteAddress,
  };
}
